import React, { useState } from 'react';
import {
  Box,
  Collapse,
  Divider,
  IconButton,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import { ValidooExportCheck } from './types';

type CardStatus = 'pass' | 'fail' | 'error';

function statusIcon(status: CardStatus, size = 18) {
  if (status === 'pass') return <CheckCircleIcon sx={{ fontSize: size, color: '#2e7d32' }} />;
  if (status === 'fail') return <CancelIcon sx={{ fontSize: size, color: '#d32f2f' }} />;
  return <WarningAmberIcon sx={{ fontSize: size, color: '#ed6c02' }} />;
}

function checkIcon(check: ValidooExportCheck) {
  const iconStatus: CardStatus = check.status === 'pass' ? 'pass' : check.status === 'error' ? 'error' : 'fail';

  return (
    <Tooltip title={`${check.label}: ${check.message}`} arrow placement="top">
      <Box sx={{ display: 'inline-flex', alignItems: 'center', cursor: 'help' }}>
        {statusIcon(iconStatus, 16)}
      </Box>
    </Tooltip>
  );
}

function getCardStatus(checks: ValidooExportCheck[]): CardStatus {
  if (checks.some((check) => check.status === 'error')) return 'error';
  if (checks.some((check) => check.status !== 'pass')) return 'fail';
  return 'pass';
}

interface ValidooExportCheckCardProps {
  title: string;
  subtitle?: string;
  checks: ValidooExportCheck[];
  metaLine?: string;
  defaultExpanded?: boolean;
}

const ValidooExportCheckCard: React.FC<ValidooExportCheckCardProps> = ({
  title,
  subtitle,
  checks,
  metaLine,
  defaultExpanded = true,
}) => {
  const [expanded, setExpanded] = useState(defaultExpanded);
  const failedChecks = checks.filter((check) => check.status !== 'pass');
  const passedChecks = checks.filter((check) => check.status === 'pass');
  const status = getCardStatus(checks);

  return (
    <Box
      sx={{
        border: '1px solid',
        borderColor: status === 'pass' ? '#c8e6c9' : status === 'error' ? '#ffe0b2' : '#ffcdd2',
        borderRadius: 1.5,
        mb: 1,
        backgroundColor: status === 'pass' ? '#f1f8e9' : status === 'error' ? '#fff8e1' : '#fff5f5',
        overflow: 'hidden',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          px: 1.5,
          py: 1,
          cursor: 'pointer',
          '&:hover': { backgroundColor: 'rgba(0,0,0,0.02)' },
        }}
        onClick={() => setExpanded((prev) => !prev)}
      >
        {statusIcon(status, 22)}

        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography
            variant="body2"
            sx={{ fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
          >
            {title}
          </Typography>
          {subtitle && (
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', display: 'block' }}
            >
              {subtitle}
            </Typography>
          )}
        </Box>

        <Stack direction="row" spacing={0.5} sx={{ flexShrink: 0 }}>
          {checks.map((check) => (
            <Box key={check.code} sx={{ display: 'inline-flex' }}>
              {checkIcon(check)}
            </Box>
          ))}
        </Stack>

        <IconButton size="small" sx={{ ml: 0.5 }}>
          {expanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
        </IconButton>
      </Box>

      <Collapse in={expanded}>
        <Divider />
        <Box sx={{ px: 2, py: 1.5 }}>
          {failedChecks.length > 0 && (
            <Box sx={{ mb: passedChecks.length > 0 ? 1.5 : 0 }}>
              <Typography
                variant="caption"
                sx={{ fontWeight: 700, color: failedChecks.some((check) => check.status === 'error') ? '#ed6c02' : '#d32f2f', textTransform: 'uppercase', letterSpacing: 0.5 }}
              >
                {failedChecks.some((check) => check.status === 'error') ? 'Fel / underkänd' : 'Underkänd'}
              </Typography>
              {failedChecks.map((check) => (
                <Box key={check.code} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, mt: 0.5 }}>
                  {checkIcon(check)}
                  <Box>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>{check.label}</Typography>
                    <Typography variant="caption" color="text.secondary">{check.message}</Typography>
                  </Box>
                </Box>
              ))}
            </Box>
          )}

          {passedChecks.length > 0 && (
            <Box>
              <Typography
                variant="caption"
                sx={{ fontWeight: 700, color: '#2e7d32', textTransform: 'uppercase', letterSpacing: 0.5 }}
              >
                Godkänd
              </Typography>
              {passedChecks.map((check) => (
                <Box key={check.code} sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.3 }}>
                  {checkIcon(check)}
                  <Typography variant="body2" color="text.secondary">{check.label}</Typography>
                </Box>
              ))}
            </Box>
          )}

          {metaLine && (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
              {metaLine}
            </Typography>
          )}
        </Box>
      </Collapse>
    </Box>
  );
};

export default ValidooExportCheckCard;
